<template>
    <div
        :id="id"
        ref="container"
        :data-testid="testId || 'inputtext-wrapper'"
        class="tl-input-wrapper"
    >
        <FloatLabel
            :id="inputId"
            :label="label"
        >
            <input
                :id="inputId"
                ref="input"
                :value="model"
                type="number"
                :disabled="disabled"
                :maxlength="maxlength"
                :step="step"
                :class="{
                    'tl-input': true,
                    'tl-input-filled': filled,
                    'tl-input-disabled': disabled,
                    'tl-input-focus': focused || parentFocused,
                    'tl-input-invalid': !!error,
                }"
                :aria-invalid="!!error || undefined"
                :placeholder="placeholder"
                @input="handleInput($event)"
                @focus="focused = true"
                @blur="focused = false"
                @keydown="handleKey"
            />
        </FloatLabel>

        <ErrorText
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useId } from 'vue'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import { isEmpty, isNil, isNotEmpty, isNotNil } from 'ramda'
import type { BaseInputProps } from '../InputText/types'
import FloatLabel from '../FloatLabel/FloatLabel.vue'
import { autoFocusOnMounted } from 'widget-components/integrations/CustomPage/autofocus'

defineOptions({ inheritAttrs: false })

type InputNumberProps = BaseInputProps & {
    initialValue?: number
    step?: number
}

const props = withDefaults(defineProps<InputNumberProps & {
    placeholder?: string
}>(), {
    step: 1,
})

const emit = defineEmits(['submit'])

const inputId = computed(() => {
    return props.id ? `${props.id}-input` : useId()
})
const model = defineModel<number | null>()
const input = ref<HTMLInputElement>()
const filled = computed(() => isNotEmpty(model.value) && isNotNil(model.value))
const focused = ref(false)

autoFocusOnMounted(
    props,
    () => input.value,
)

const handleInput = (event: Event) => {
    const val = (event.target as HTMLInputElement).value
    model.value = isNil(val) ? null : parseFloat(val)
}

onMounted(() => {
    if (!isNil(props.initialValue) && !isEmpty(props.initialValue)) {
        model.value = props.initialValue
    }
})

const handleKey = (event: KeyboardEvent) => {
    const code = event.code

    // ENTER
    if (code === 'Enter') {
        emit('submit')
    }
}
</script>

<style scoped lang="sass">
@import '../../style/components/input-base.sass'
</style>
